import { useCallback, useState } from "react";
import {
  NoticeItem,
  NoticeListResponse,
  NoticeCreateRequest,
  NoticeModifyRequest,
  BaseResponse,
} from "../../types/notice";
import { api } from "../../utils/api";

interface ServiceNoticeState {
  noticeList: NoticeItem[];
  totalCount: number;
  currentPage: number;
}

export const useServiceNotice = () => {
  const [state, setState] = useState<ServiceNoticeState>({
    noticeList: [],
    totalCount: 0,
    currentPage: 1,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleError = (error: any) => {
    const errorMessage =
      error?.response?.data?.message ||
      error?.message ||
      "알 수 없는 오류가 발생했습니다.";
    setError(errorMessage);
  };

  // 공지사항 리스트 조회 (10개씩)
  const getNoticeList = useCallback(async (page: number = 1) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.get<NoticeListResponse>(
        `/admin/v1/notice/list?page=${page}`
      );

      if (response.data.status) {
        setState((prev) => ({
          ...prev,
          noticeList: response.data.data,
          totalCount: response.data.data.length,
          currentPage: page,
        }));
      } else {
        throw new Error("API 응답 상태가 실패입니다.");
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 공지사항 생성
  const createNotice = useCallback(
    async (noticeData: NoticeCreateRequest) => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await api.post<BaseResponse>(
          `/admin/v1/notice/gen`,
          noticeData
        );

        if (response.data.status) {
          await getNoticeList(state.currentPage);
          return true;
        } else {
          throw new Error("공지사항 생성에 실패했습니다.");
        }
      } catch (error) {
        handleError(error);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [getNoticeList, state.currentPage]
  );

  // 공지사항 수정
  const updateNotice = useCallback(
    async (
      id: string,
      updateData: Omit<NoticeCreateRequest, "reference_image"> & {
        reference_image?: string;
      }
    ) => {
      setIsLoading(true);
      setError(null);

      try {
        const requestData: NoticeModifyRequest = {
          id,
          title: updateData.title,
          description: updateData.description,
          reference_image: updateData.reference_image || "",
          delete: false,
        };

        const response = await api.post<BaseResponse>(
          `/admin/v1/notice/update`,
          requestData
        );

        if (response.data.status) {
          await getNoticeList(state.currentPage);
          return true;
        } else {
          throw new Error("공지사항 수정에 실패했습니다.");
        }
      } catch (error) {
        handleError(error);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [getNoticeList, state.currentPage]
  );

  const resetState = useCallback(() => {
    setState({
      noticeList: [],
      totalCount: 0,
      currentPage: 1,
    });
    setError(null);
  }, []);

  // 페이지 변경
  const setCurrentPage = useCallback((page: number) => {
    setState((prev) => ({
      ...prev,
      currentPage: page,
    }));
  }, []);

  return {
    noticeList: state.noticeList,
    totalCount: state.totalCount,
    currentPage: state.currentPage,

    isLoading,
    error,

    getNoticeList,
    createNotice,
    updateNotice,

    resetState,
    setCurrentPage,

    totalPages: Math.ceil(state.totalCount / 10),
    hasNotices: state.noticeList.length > 0,
    isEmpty: state.noticeList.length === 0 && !isLoading,
  };
};
